<script lang="ts" setup>
import { i18n } from '@/i18n';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { SelectBoxNames } from './domains/entities/selectBoxNames';

const t = i18n.t;

const props = withDefaults(
  defineProps<{
    index: number;
    modelValue: string;
    propOptions: string[];
    selectBoxName: SelectBoxNames;
    customUnitValue?: string;
    displayAllow?: boolean;
    disabled?: boolean;
  }>(),
  { displayAllow: false, disabled: false, customUnitValue: undefined }
);

const selected = ref<string>(props.modelValue);
const isOpen = ref(false);

const result = ref<DOMRect>();
const xLength = ref(0);
const yLength = ref(0);
const containerWidth = ref(0);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

watch(
  () => props.modelValue,
  () => {
    selected.value = props.modelValue;
  }
);

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  if (!props.disabled) {
    isOpen.value = !isOpen.value;
  }
  if (isOpen.value) {
    const element = document.getElementById(`${props.selectBoxName}-simple-select-container${props.index}`);
    if (!element) return;
    result.value = element.getBoundingClientRect();
    xLength.value = result.value.left;
    yLength.value = result.value.bottom;
    containerWidth.value = result.value.width;
  }
};

/**
 * オプションを選択し、親コンポーネントに選択値を通知します。
 * @param option - 選択されたオプション
 */
const selectOption = (option: string) => {
  selected.value = option;
  emit('update:modelValue', option);
  isOpen.value = false;
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!(event.target as Element).closest(`#${props.selectBoxName}-simple-select-${props.index}`)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div :id="`${props.selectBoxName}-simple-select-${index}`" class="simple-select-box">
    <div
      :id="`${props.selectBoxName}-simple-select-container${index}`"
      class="simple-select-box__selected"
      :class="{ 'simple-select-box__selected--disabled': disabled }"
      @click="toggleDropdown"
    >
      <span class="simple-select-box__field" :class="`simple-select-box__field--${selected}`">
        <template v-if="customUnitValue === undefined">
          {{ t(selected) }}
        </template>
        <template v-else>
          {{ t(selected) + t(customUnitValue) }}
        </template>
      </span>
      <SvgLoader v-if="displayAllow" name="arrowDown" class="simple-select-box__arrow" />
    </div>
    <Teleport v-if="isOpen" to="body">
      <div
        class="simple-select-box__dropdown"
        :style="{
          top: `${yLength}px`,
          left: `${xLength}px`,
          width: `${containerWidth}px`,
        }"
      >
        <div v-for="option in propOptions" :key="option" class="simple-select-box__option" @click="selectOption(option)">
          <template v-if="customUnitValue === undefined">
            {{ t(option) }}
          </template>
          <template v-else>
            {{ t(option) + t(customUnitValue) }}
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.simple-select-box {
  justify-items: center;
  position: relative;

  &__field {
    width: 100%;
    height: 100%;
    display: grid; // これが無いと上下中央揃えできない
    place-items: center;
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    letter-spacing: 0em;
    font-weight: 400;
    text-align: center;
    background-color: transparent;
  }

  &__selected {
    width: 100%;
    height: 100%;
    color: $textDefault;
    cursor: pointer;
    display: grid;
    grid-template-columns: auto fit-content(100%);
    place-items: center;

    &--disabled {
      background-color: $gray30;
      cursor: not-allowed;
    }
  }

  &__arrow {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 100%;
    width: 24px;
  }

  &__dropdown {
    position: absolute;
    background-color: $semanticWhite;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1000; // モーダルより高い値に設定
    height: 120px;
    overflow-y: auto;
  }

  &__option {
    padding: 10px;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;
    }
  }
}
</style>
